import { x } from '@xstyled/styled-components';
import React, { Children } from 'react';
import { Item, ItemDefinition, registry, RenderProps } from '../Item';
import { LAYOUT_GRID } from '../itemIds';
import { useEditorStore } from '~/shell/Store';

interface Props {
  gridTemplateColumns?: string | { _: string; lg: string };
  gridTemplateRows?: string | { _: string; lg: string };
  gridAutoFlow?:
    'row' | 'column' | { _: 'row' | 'column'; lg: 'row' | 'column' };
}

export type LayoutGridDefinition = ItemDefinition<typeof LAYOUT_GRID, Props>;

export class LayoutGrid extends Item<typeof LAYOUT_GRID, Props> {
  protected render({
    gridTemplateColumns,
    gridTemplateRows,
    gridAutoFlow,
    children
  }: RenderProps<Props>) {
    const { contentEditable } = useEditorStore();
    const childArray = Children.toArray(children);

    if (!gridTemplateColumns) {
      gridTemplateColumns = {
        _: 'minmax(0, 1fr)',
        lg: 'repeat(2, minmax(0, 1fr))'
      };
    }

    if (!gridTemplateRows) {
      gridTemplateRows = {
        _: `repeat(${childArray.length}, auto)`,
        lg: `repeat(${Math.ceil(childArray.length / 2)}, 300px)`
      };
    }

    const resolvedGridAutoFlow = gridAutoFlow ?? {
      _: 'row' as const,
      lg: 'column' as const
    };

    return (
      <x.div
        w="100%"
        maxWidth="1500px"
        h="100%"
        px={{ _: 'md', md: 'md', lg: 'xl' }}
        py={{ _: 'xxxl', md: 'xl', lg: 'xl' }}
        gap={{ _: 'md', md: 'md', lg: 'xl' }}
        mx="auto"
        display="grid"
        alignContent="center"
        gridAutoFlow={resolvedGridAutoFlow}
        gridTemplateColumns={gridTemplateColumns}
        gridTemplateRows={gridTemplateRows}
        aria-live="polite"
        style={contentEditable ? { paddingTop: '100px' } : undefined}
      >
        {childArray.map((c, i) => (
          <x.div key={i} minWidth={0} overflow="hidden">
            {c}
          </x.div>
        ))}
      </x.div>
    );
  }
}

registry[LAYOUT_GRID] = LayoutGrid;
